import React, { createContext, useContext, useState, useEffect } from "react";

// ---------------------------------------------------------------------------
// Supported languages — India's major scheduled / official languages.
// Each entry: code, English name, native script label, short 2-letter code.
// ---------------------------------------------------------------------------
export const LANGUAGES = [
  { code: "en", name: "English",   native: "English",   short: "EN", glyph: "A"  },
  { code: "hi", name: "Hindi",     native: "हिन्दी",      short: "HI", glyph: "अ" },
  { code: "te", name: "Telugu",    native: "తెలుగు",      short: "TE", glyph: "అ"  },
  { code: "ta", name: "Tamil",     native: "தமிழ்",        short: "TA", glyph: "அ" },
  { code: "kn", name: "Kannada",   native: "ಕನ್ನಡ",        short: "KN", glyph: "ಅ" },
  { code: "ml", name: "Malayalam", native: "മലയാളം",      short: "ML", glyph: "അ" },
  { code: "bn", name: "Bengali",   native: "বাংলা",        short: "BN", glyph: "অ" },
  { code: "mr", name: "Marathi",   native: "मराठी",        short: "MR", glyph: "अ" },
  { code: "gu", name: "Gujarati",  native: "ગુજરાતી",       short: "GU", glyph: "અ" },
  { code: "pa", name: "Punjabi",   native: "ਪੰਜਾਬੀ",        short: "PA", glyph: "ਅ" },
  { code: "or", name: "Odia",      native: "ଓଡ଼ିଆ",         short: "OR", glyph: "ଅ" },
  { code: "ur", name: "Urdu",      native: "اُردُو",         short: "UR", glyph: "ا" },
];

// Translations — English + Telugu are full; other Indian languages have the
// most-visible strings translated (brand, hero CTAs, auth buttons) so users
// feel "seen". Missing keys auto-fall back to English via the `t()` helper.
const dict = {
  en: {
    brand: "NeuroOpinion India",
    tagline: "Expert neuro second opinions from India's top specialists.",
    cta_get_opinion: "Get Second Opinion",
    cta_join_doctor: "Join as a Doctor",
    login: "Login", signup: "Sign up", logout: "Logout",
    dashboard: "Dashboard",
    how_it_works: "How it works",
    upload_scans: "Upload Scans",
    book_consultation: "Book Consultation",
    symptoms: "Symptoms",
    my_cases: "My Cases",
    patients: "Patients", doctors: "Doctors",
    pending_approvals: "Pending Approvals",
    analytics: "Analytics",
    email: "Email", password: "Password", phone: "Mobile number",
    name: "Full name", otp: "OTP",
    send_otp: "Send OTP", verify_otp: "Verify OTP",
    patient: "Patient", doctor: "Doctor",
    choose_doctor: "Choose a doctor",
    fee: "Fee", pay_now: "Pay Now",
    chat: "Chat", send: "Send",
    opinion: "Second Opinion", write_opinion: "Write Opinion",
    specialization: "Specialization", reg_no: "MCI/NMC Registration",
    experience: "Experience (years)", license: "Upload License",
    consultation_fee: "Consultation fee (₹)",
    status_pending: "Pending", status_approved: "Approved", status_rejected: "Rejected",
    approve: "Approve", reject: "Reject",
    home: "Home",
  },
  te: {
    brand: "న్యూరోఒపీనియన్ ఇండియా",
    tagline: "భారతదేశపు అగ్రగామి నిపుణుల నుండి న్యూరో సెకండ్ ఒపీనియన్.",
    cta_get_opinion: "సెకండ్ ఒపీనియన్ పొందండి",
    cta_join_doctor: "వైద్యుడిగా చేరండి",
    login: "లాగిన్", signup: "సైన్ అప్", logout: "లాగౌట్",
    dashboard: "డాష్‌బోర్డ్",
    how_it_works: "ఎలా పని చేస్తుంది",
    upload_scans: "స్కాన్‌లు అప్‌లోడ్ చేయండి",
    book_consultation: "కన్సల్టేషన్ బుక్ చేయండి",
    symptoms: "లక్షణాలు",
    my_cases: "నా కేసులు",
    patients: "రోగులు", doctors: "వైద్యులు",
    pending_approvals: "పెండింగ్ ఆమోదాలు",
    analytics: "విశ్లేషణలు",
    email: "ఇమెయిల్", password: "పాస్‌వర్డ్", phone: "మొబైల్ నంబర్",
    name: "పూర్తి పేరు", otp: "ఓటీపీ",
    send_otp: "ఓటీపీ పంపండి", verify_otp: "ఓటీపీ ధృవీకరించండి",
    patient: "రోగి", doctor: "వైద్యుడు",
    choose_doctor: "వైద్యుడిని ఎంచుకోండి",
    fee: "రుసుము", pay_now: "ఇప్పుడే చెల్లించండి",
    chat: "చాట్", send: "పంపండి",
    opinion: "సెకండ్ ఒపీనియన్", write_opinion: "ఒపీనియన్ రాయండి",
    specialization: "ప్రత్యేకత", reg_no: "MCI/NMC రిజిస్ట్రేషన్",
    experience: "అనుభవం (సంవత్సరాలు)", license: "లైసెన్స్ అప్‌లోడ్",
    consultation_fee: "కన్సల్టేషన్ రుసుము (₹)",
    status_pending: "పెండింగ్", status_approved: "ఆమోదించబడింది", status_rejected: "తిరస్కరించబడింది",
    approve: "ఆమోదించండి", reject: "తిరస్కరించండి",
    home: "హోమ్",
  },
  hi: {
    brand: "न्यूरोओपिनियन इंडिया",
    tagline: "भारत के शीर्ष विशेषज्ञों से न्यूरो सेकंड ओपिनियन।",
    cta_get_opinion: "सेकंड ओपिनियन लें",
    cta_join_doctor: "डॉक्टर के रूप में जुड़ें",
    login: "लॉगिन", signup: "साइन अप", logout: "लॉगआउट",
    dashboard: "डैशबोर्ड",
    how_it_works: "यह कैसे काम करता है",
    patient: "मरीज़", doctor: "डॉक्टर",
  },
  ta: {
    brand: "நியூரோ ஒப்பீனியன் இந்தியா",
    tagline: "இந்தியாவின் சிறந்த நிபுணர்களிடமிருந்து நியூரோ இரண்டாம் கருத்து.",
    cta_get_opinion: "இரண்டாம் கருத்து பெறுக",
    cta_join_doctor: "மருத்துவராக இணையுங்கள்",
    login: "உள்நுழைய", signup: "பதிவுசெய்", logout: "வெளியேறு",
    dashboard: "டாஷ்போர்டு",
    patient: "நோயாளி", doctor: "மருத்துவர்",
  },
  kn: {
    brand: "ನ್ಯೂರೋಒಪಿನಿಯನ್ ಇಂಡಿಯಾ",
    tagline: "ಭಾರತದ ಉನ್ನತ ತಜ್ಞರಿಂದ ನ್ಯೂರೋ ಎರಡನೆಯ ಅಭಿಪ್ರಾಯ.",
    cta_get_opinion: "ಎರಡನೇ ಅಭಿಪ್ರಾಯ ಪಡೆಯಿರಿ",
    cta_join_doctor: "ವೈದ್ಯರಾಗಿ ಸೇರಿ",
    login: "ಲಾಗಿನ್", logout: "ಲಾಗ್ಔಟ್",
    patient: "ರೋಗಿ", doctor: "ವೈದ್ಯ",
  },
  ml: {
    brand: "ന്യൂറോഒപിനിയൻ ഇന്ത്യ",
    tagline: "ഇന്ത്യയിലെ മികച്ച വിദഗ്ധരിൽ നിന്ന് ന്യൂറോ രണ്ടാം അഭിപ്രായം.",
    cta_get_opinion: "രണ്ടാം അഭിപ്രായം നേടുക",
    cta_join_doctor: "ഡോക്ടറായി ചേരുക",
    login: "ലോഗിൻ", logout: "ലോഗൗട്ട്",
    patient: "രോഗി", doctor: "ഡോക്ടർ",
  },
  bn: {
    brand: "নিউরোঅপিনিয়ন ইন্ডিয়া",
    tagline: "ভারতের শীর্ষ বিশেষজ্ঞদের কাছ থেকে নিউরো সেকেন্ড ওপিনিয়ন।",
    cta_get_opinion: "সেকেন্ড ওপিনিয়ন নিন",
    cta_join_doctor: "ডাক্তার হিসেবে যোগ দিন",
    login: "লগইন", logout: "লগআউট",
    patient: "রোগী", doctor: "ডাক্তার",
  },
  mr: {
    brand: "न्यूरोओपिनियन इंडिया",
    tagline: "भारतातील आघाडीच्या तज्ञांकडून न्यूरो दुसरे मत.",
    cta_get_opinion: "दुसरे मत मिळवा",
    cta_join_doctor: "डॉक्टर म्हणून सामील व्हा",
    login: "लॉगिन", logout: "लॉगआउट",
    patient: "रुग्ण", doctor: "डॉक्टर",
  },
  gu: {
    brand: "ન્યૂરોઓપિનિયન ઇન્ડિયા",
    tagline: "ભારતના અગ્રણી નિષ્ણાતો પાસેથી ન્યૂરો બીજી અભિપ્રાય.",
    cta_get_opinion: "બીજી અભિપ્રાય મેળવો",
    cta_join_doctor: "ડૉક્ટર તરીકે જોડાઓ",
    login: "લૉગિન", logout: "લૉગઆઉટ",
    patient: "દર્દી", doctor: "ડૉક્ટર",
  },
  pa: {
    brand: "ਨਿਊਰੋਓਪੀਨੀਅਨ ਇੰਡੀਆ",
    tagline: "ਭਾਰਤ ਦੇ ਚੋਟੀ ਦੇ ਮਾਹਿਰਾਂ ਤੋਂ ਨਿਊਰੋ ਦੂਜੀ ਰਾਏ।",
    cta_get_opinion: "ਦੂਜੀ ਰਾਏ ਲਵੋ",
    cta_join_doctor: "ਡਾਕਟਰ ਵਜੋਂ ਸ਼ਾਮਲ ਹੋਵੋ",
    login: "ਲੌਗਇਨ", logout: "ਲੌਗਆਊਟ",
    patient: "ਮਰੀਜ਼", doctor: "ਡਾਕਟਰ",
  },
  or: {
    brand: "ନ୍ୟୁରୋଅପିନିଅନ ଇଣ୍ଡିଆ",
    tagline: "ଭାରତର ଶୀର୍ଷ ବିଶେଷଜ୍ଞଙ୍କଠାରୁ ନ୍ୟୁରୋ ଦ୍ୱିତୀୟ ମତ।",
    cta_get_opinion: "ଦ୍ୱିତୀୟ ମତ ନିଅନ୍ତୁ",
    cta_join_doctor: "ଡାକ୍ତର ଭାବରେ ଯୋଗ ଦିଅନ୍ତୁ",
    login: "ଲଗଇନ୍", logout: "ଲଗଆଉଟ୍",
    patient: "ରୋଗୀ", doctor: "ଡାକ୍ତର",
  },
  ur: {
    brand: "نیوروآپینین انڈیا",
    tagline: "بھارت کے سر فہرست ماہرین سے نیورو دوسری رائے۔",
    cta_get_opinion: "دوسری رائے حاصل کریں",
    cta_join_doctor: "ڈاکٹر کے طور پر شامل ہوں",
    login: "لاگ ان", logout: "لاگ آؤٹ",
    patient: "مریض", doctor: "ڈاکٹر",
  },
};

const I18nCtx = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => {
    const saved = localStorage.getItem("noi_lang");
    if (saved && LANGUAGES.some((l) => l.code === saved)) return saved;
    // First visit → auto-detect from browser locale.
    // navigator.language is like "hi-IN", "te-IN", "ta", etc.
    try {
      const candidates = [navigator.language, ...(navigator.languages || [])]
        .filter(Boolean).map((x) => x.toLowerCase());
      for (const c of candidates) {
        const prefix = c.split("-")[0]; // "hi-in" -> "hi"
        if (LANGUAGES.some((l) => l.code === prefix)) return prefix;
      }
    } catch {}
    return "en";
  });
  useEffect(() => {
    localStorage.setItem("noi_lang", lang);
    // Set HTML lang + direction (Urdu is RTL) for a11y and correct text rendering
    try {
      document.documentElement.lang = lang;
      document.documentElement.dir = lang === "ur" ? "rtl" : "ltr";
    } catch {}
  }, [lang]);
  const t = (k) => dict[lang]?.[k] ?? dict.en[k] ?? k;
  return <I18nCtx.Provider value={{ lang, setLang, t }}>{children}</I18nCtx.Provider>;
}
export const useI18n = () => useContext(I18nCtx);
